export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: number;
  image: string;
  keywords: string[];
  relatedSlugs: string[];
  /** Public Supabase Storage URL for the campaign comparison PDF, if available. */
  pdfUrl?: string;
  /** Public CSVs for the experiment, if available. */
  csvDownloads?: { label: string; url: string }[];
  /** 'storm' | 'breakdown' for auto-published experiment posts, undefined for hand-written. */
  experimentType?: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: "1",
    slug: "reduce-google-ads-cpa",
    title: "How to Reduce Google Ads CPA: 7 Strategies That Work",
    excerpt: "Cut your cost-per-acquisition in half. Proven strategies used by agencies managing $100M+ in ad spend.",
    category: "Budget Optimization",
    author: "George Woodworth",
    date: "2026-04-01",
    readTime: 8,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    keywords: ["Google Ads CPA", "cost per acquisition", "Google Ads optimization", "reduce costs"],
    relatedSlugs: ["budget-optimization-guide", "keyword-filtering-google-ads"],
    content: `# How to Reduce Google Ads CPA: 7 Strategies That Work

Your Google Ads campaigns are costing too much per conversion. You're not alone—most businesses waste 40-60% of their ad budget on low-quality clicks that never convert.

The good news? CPA reduction doesn't require cutting your budget. It requires smarter keyword selection, tighter targeting, and AI-driven optimization.

Here are 7 proven strategies to cut your cost-per-acquisition in half:

## 1. Filter Keywords by Budget-Feasibility

Not all keywords are worth bidding on. A keyword that costs $50 per click but brings $0 in revenue is burning your budget.

**The Solution**: Use real Google Ads bid data to calculate the cost of each keyword. If a keyword costs $50/click and your average order value is $100, that keyword won't be profitable.

Upfixe does this automatically—filtering out expensive keywords that won't convert before campaigns launch.

## 2. Implement Strict Negative Keywords

Negative keywords prevent wasted clicks. Without them, your ads show for irrelevant searches.

Example: If you sell premium consulting, add negatives like:
- "free"
- "cheap"
- "DIY"
- "budget"

This alone can reduce CPA by 15-25%.

## 3. Use Audience Targeting, Not Just Keywords

Keywords alone don't guarantee qualified traffic. People searching for your keyword might not be your target customer.

**Better approach**: Layer in audience targeting:
- Similar audiences to your best customers
- In-market audiences
- Custom intent audiences

## 4. Optimize Landing Pages for Conversion

Your ad copy could be perfect, but if your landing page is slow or confusing, people bounce. Bounces = wasted ad spend.

**Action items**:
- Ensure landing page loads in <3 seconds
- Single clear CTA above the fold
- Match ad copy to landing page headline

## 5. Test Different Ad Copy Angles

Different headlines resonate with different audiences. The angle that works for agencies might not work for freelancers.

Test variations like:
- Pain-focused: "Stop wasting budget on inefficient keywords"
- Solution-focused: "Cut your Google Ads costs by 50%"
- Proof-focused: "Agencies managing $100M+ use our system"

## 6. Use Conversion Value Tracking

Without proper conversion tracking, Google's algorithm can't optimize correctly. It's like flying blind.

Set up conversion value tracking so Google knows:
- Which clicks led to sales
- How much each conversion is worth
- Which audiences are most valuable

## 7. Implement AI-Driven Budget Allocation

Manual budget allocation across keywords is inefficient. AI can adjust in real-time based on performance.

AI looks at:
- Historical conversion data
- Real-time performance trends
- Seasonal patterns
- Competitive bid changes

The result? Budget flows automatically to your best-performing keywords.

## The Results You Can Expect

Agencies using all 7 strategies typically see:
- 40-60% reduction in CPA within 90 days
- 20-30% increase in total conversions
- 50%+ improvement in ROI

The key is implementation. Start with strategies 1-3 this week. Add 4-6 next month. Implement 7 as soon as your conversion tracking is solid.

Your CPA won't drop overnight, but within 90 days, you'll see dramatic improvement.`
  },
  {
    id: "2",
    slug: "budget-optimization-guide",
    title: "Google Ads Budget Optimization: Complete Guide for 2026",
    excerpt: "Everything you need to know about optimizing your Google Ads budget for maximum ROI.",
    category: "Budget Optimization",
    author: "George Woodworth",
    date: "2026-04-02",
    readTime: 10,
    image: "https://images.unsplash.com/photo-1460925895917-adf4e565db6d?w=800",
    keywords: ["Google Ads budget", "budget optimization", "ad spend", "ROI"],
    relatedSlugs: ["reduce-google-ads-cpa", "keyword-filtering-google-ads"],
    content: `# Google Ads Budget Optimization: Complete Guide for 2026

Your Google Ads budget is your most valuable asset. Spend it wisely, and you'll see explosive growth. Waste it, and you'll hemorrhage money on low-quality clicks.

This guide covers everything you need to optimize your budget in 2026.

## Understanding Your Budget Structure

Google Ads has three budget levels:
1. **Daily Budget** - How much you're willing to spend per day
2. **Monthly Budget** - Calculated by daily budget × 30.4
3. **Keyword Budget** - The maximum you'll spend on any single keyword

Most businesses get this wrong. They set a high daily budget, then watch it burn on expensive keywords that don't convert.

## The Budget Optimization Framework

### Step 1: Calculate Your Break-Even Cost Per Click

Before you spend a penny, know your maximum allowable CPC (cost per click).

Formula:
\`\`\`
Break-Even CPC = Average Order Value ÷ Conversion Rate

Example:
- AOV: $500
- Conversion Rate: 2% (1 in 50 clicks)
- Break-Even CPC: $10
\`\`\`

If your average CPC is $15, you're losing money on every click.

### Step 2: Segment Campaigns by Performance

Not all keywords are equal. Some convert. Some don't.

Segment your campaigns:
- **High Performers**: Converts at >3%. Increase budget.
- **Medium Performers**: Converts at 1-3%. Optimize copy.
- **Low Performers**: Converts at <1%. Pause or rewrite.

### Step 3: Implement Keyword-Level Budget Caps

The single biggest budget leak: high-CPC keywords without caps.

Set maximum CPCs:
- High-volume keywords: $5-10
- Medium-volume keywords: $10-20
- Low-volume keywords: $20+

This prevents any single keyword from draining your budget.

### Step 4: Use AI for Real-Time Optimization

Manual optimization happens once per week. AI optimization happens every hour.

AI-driven platforms:
- Monitor performance in real-time
- Adjust bids based on conversion likelihood
- Shift budget to top performers
- Pause underperformers automatically

## Common Budget Mistakes (And How to Fix Them)

**Mistake 1: Spreading budget too thin**
- **Problem**: $1,000/month across 500 keywords = $2/keyword. Not enough for testing.
- **Solution**: Focus on top 50 keywords. $20/keyword for proper testing.

**Mistake 2: Not accounting for seasonality**
- **Problem**: Same budget year-round. Q4 is 3x more competitive.
- **Solution**: Increase budget 40% in Q4, reduce in Q1.

**Mistake 3: Ignoring conversion value**
- **Problem**: Treating all conversions equally. But a $100 order and a $10,000 order are different.
- **Solution**: Weight budget allocation by conversion value, not click volume.

## Budget Allocation by Business Type

### B2B Services
- Budget: 70% branded, 30% high-intent keywords
- Focus: Quality over quantity
- Expected: 5-15 leads/month per $1,000 spend

### E-Commerce
- Budget: 50% branded, 50% product-focused
- Focus: Keywords with clear purchase intent
- Expected: 0.5-2% conversion rate

### SaaS
- Budget: 60% branded, 40% Free Plan keywords
- Focus: Trial signups, not product awareness
- Expected: 2-8% conversion rate

## Tools for Budget Optimization

1. **Google Ads Scripts** - Automate bid adjustments
2. **Conversion Tracking** - Essential for ROI calculation
3. **Analytics Integration** - See actual revenue impact
4. **AI Platforms** - Real-time optimization

## Implementation Timeline

**Week 1**: Calculate break-even CPC, segment campaigns
**Week 2**: Implement keyword-level budget caps
**Week 3**: Set up AI optimization or bid automation
**Week 4**: Review, adjust, optimize

By month 2, you should see 20-30% improvement in ROI.

## The Bottom Line

Budget optimization isn't complicated. It's:
1. Know your break-even CPC
2. Segment by performance
3. Cap expensive keywords
4. Use AI for real-time optimization

Follow these principles, and your budget will work harder than ever.`
  },
  {
    id: "3",
    slug: "keyword-filtering-google-ads",
    title: "Keyword Filtering for Google Ads: Stop Wasting Budget on Bad Keywords",
    excerpt: "The #1 reason Google Ads fails: bidding on keywords that never convert. Here's how to filter them out.",
    category: "Google Ads Tips",
    author: "George Woodworth",
    date: "2026-04-03",
    readTime: 7,
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
    keywords: ["keyword filtering", "Google Ads keywords", "keyword research", "negative keywords"],
    relatedSlugs: ["reduce-google-ads-cpa", "budget-optimization-guide"],
    content: `# Keyword Filtering for Google Ads: Stop Wasting Budget on Bad Keywords

You're bidding on keywords that don't convert. Every month, thousands of clicks go to people who will never buy.

This costs you money. Lots of it.

The solution? Intelligent keyword filtering.

## Why Most Keyword Filtering Fails

Most businesses filter keywords by search volume or competition. That's backwards.

**Search volume tells you nothing about profitability.** A high-volume keyword could cost $100/click and generate zero sales.

Real keyword filtering is based on:
- Cost per click
- Conversion probability
- Revenue per conversion
- Your budget

## The Keyword Filtering Framework

### Step 1: Know Your Bid Capacity

Before filtering, calculate the maximum you can bid on any keyword:

\`\`\`
Max Bid = (Average Order Value × Conversion Rate) - Cost of Fulfillment
\`\`\`

Example:
- AOV: $500
- Conversion Rate: 2%
- Fulfillment Cost: $100
- Max Bid: (500 × 0.02) - 100 = -90

**Wait, negative?** This business shouldn't bid on this keyword. It's not profitable.

Let's adjust:
- AOV: $2,000
- Conversion Rate: 2%
- Fulfillment Cost: $500
- Max Bid: (2,000 × 0.02) - 500 = $40/click

Now you can bid up to $40/click and still be profitable.

### Step 2: Pull Real Bid Data

Google Ads Keyword Planner shows "estimated" bids. They're usually wrong.

Use real data from:
- **Your own campaigns** - See actual CPCs
- **Google Auction Insights** - Competitor average CPCs
- **Bid simulation tools** - What you'd pay for higher positions
- **Market research** - Industry benchmarks

### Step 3: Calculate Profitability for Each Keyword

For each keyword, determine:
1. Estimated CPC
2. Estimated conversion rate
3. Revenue per conversion
4. Profitability

Example:
- Keyword: "Google Ads agency"
- CPC: $25
- Conversion Rate: 5%
- Revenue: $5,000
- Profitability: (5,000 × 0.05) - 25 = $225 per click

This keyword is profitable. Bid on it.

Compare to:
- Keyword: "Google Ads training"
- CPC: $15
- Conversion Rate: 0.5%
- Revenue: $200
- Profitability: (200 × 0.005) - 15 = -14.99

This keyword loses money. Don't bid on it.

### Step 4: Implement Negative Keywords

For every keyword you bid on, add 5-10 negative keywords that might match.

Examples:
- Bidding on: "Google Ads agency"
- Negatives: -free, -cheap, -DIY, -tutorial, -course

This prevents wasted clicks on people looking for cheap/free alternatives.

## Common Filtering Mistakes

**Mistake 1: Filtering too aggressively**
- You eliminate keywords that have potential
- Solution: Run each keyword for 100-200 clicks before filtering

**Mistake 2: Not accounting for learning phase**
- Google Ads needs data to optimize
- New keywords have high CPCs initially
- Solution: Give keywords 2-4 weeks before filtering

**Mistake 3: Using search volume as a filter**
- High-volume keywords can be money-losers
- Solution: Use profitability, not volume

## Tools for Keyword Filtering

1. **Google Ads Scripts** - Automate filtering
2. **Keyword Analysis Tools** - Calculate profitability
3. **Competitor Tools** - See competitor bid strategy
4. **AI Platforms** - Automatic filtering and optimization

## Implementation Steps

1. **Week 1**: Calculate bid capacity for your business
2. **Week 2**: Pull real bid data from Google Ads
3. **Week 3**: Calculate profitability for current keywords
4. **Week 4**: Implement negative keywords and bid caps

Within 30 days, your cost-per-acquisition should drop 20-30%.

## The Bottom Line

Keyword filtering isn't about eliminating keywords. It's about being smart with your budget.

Bid on keywords that convert. Filter out keywords that don't. Let AI optimize in real-time.

Do this consistently, and your Google Ads ROI will improve dramatically.`
  },
  {
    id: "4",
    slug: "ai-vs-manual-google-ads",
    title: "AI vs Manual Google Ads Management: Which Should You Choose?",
    excerpt: "AI-powered Google Ads vs hiring an agency. Cost comparison and results breakdown.",
    category: "Google Ads Tips",
    author: "George Woodworth",
    date: "2026-04-04",
    readTime: 9,
    image: "https://images.unsplash.com/photo-1677442d019e157be52e69ae3630e2c90637c71346a71bb4287aff92162270c1?w=800",
    keywords: ["AI Google Ads", "Google Ads management", "agency vs AI", "automation"],
    relatedSlugs: ["reduce-google-ads-cpa", "budget-optimization-guide"],
    content: `# AI vs Manual Google Ads Management: Which Should You Choose?

You have a choice:

1. **Hire a Google Ads agency** - $3,000-10,000/month
2. **Use an AI platform** - $99-599/month
3. **Do it yourself** - Free (but your time costs money)

The question isn't which is cheapest. It's which gets you better results.

## Cost Comparison

### Agency Model
- Monthly cost: $5,000 (average)
- Annual cost: $60,000
- Minimum contract: Usually 3-6 months
- Hidden costs: Setup fees, meeting time, revision fees

**Total first-year cost: $65,000-90,000**

### AI Platform Model
- Monthly cost: $249 (Agency plan)
- Annual cost: $2,490 (on annual billing — ~17% off)
- Setup time: 15 minutes
- Hidden costs: None

**Total first-year cost: $948**

### DIY Model
- Monthly cost: $0
- Hidden cost: Your time
- Average time per week: 10-15 hours
- Your hourly rate: Probably $50-200/hour
- **Annual time cost: $26,000-156,000**

**Actual total cost: Much higher than you think**

## Results Comparison

### Agency Model
- **Pros**:
  - Dedicated account manager
  - Strategic planning
  - Regular optimization
  - Phone support

- **Cons**:
  - Slower optimization (weekly, not real-time)
  - Minimum spend requirements
  - Less transparent on what they're doing
  - You're one client of 50+

- **Typical Results**:
  - 2-4 week onboarding
  - 20-30% improvement in ROI after 90 days
  - Consistency depends on account manager quality

### AI Platform Model
- **Pros**:
  - Real-time optimization (every hour)
  - Transparent algorithms
  - No minimum spend
  - Same quality regardless of budget size

- **Cons**:
  - No phone support (usually email/chat)
  - No strategic consulting
  - You need to implement (though it's simple)

- **Typical Results**:
  - Immediate implementation
  - 30-50% improvement in ROI after 90 days
  - Consistent results at any budget level

### DIY Model
- **Pros**:
  - Complete control
  - Learning opportunity
  - No monthly fees

- **Cons**:
  - Steep learning curve
  - Time-consuming
  - Slower optimization
  - Easy to make expensive mistakes

- **Typical Results**:
  - High error rate for beginners
  - Improvement only after 6+ months
  - Inconsistent results based on your knowledge

## The Real Question: Time vs Money

**If you have <$2,000/month ad spend:**
- Use AI platform (Upfixe Solo $99/month)
- DIY is too time-consuming
- Agency minimum spend is too high

**If you have $2,000-10,000/month ad spend:**
- Choose between agency ($5,000/month) or AI (Upfixe Agency $249/month)
- AI gives better ROI at lower cost
- Agency adds strategic value if they're excellent

**If you have $10,000+/month ad spend:**
- Agency makes sense for strategic oversight
- But layer AI on top for real-time optimization
- Best combination: Agency for strategy + AI for execution

## Case Study: Upfixe AI Platform

Let's compare real numbers:

**Company Profile**:
- Monthly ad spend: $5,000
- Prior ROI: 2:1 (2x return on ad spend)
- Team size: 1 person part-time on ads

**Agency Route**:
- Monthly cost: $4,000
- Monthly benefit: (5,000 × 2) = 10,000
- Agency fee: 4,000
- Net benefit: 6,000
- Time saved: 8 hours/week

**AI Platform Route**:
- Monthly cost: $249 (Upfixe Agency)
- Monthly benefit: (5,000 × 3) = 15,000 (50% ROI improvement)
- AI cost: 249
- Net benefit: 14,751
- Time saved: 4 hours/week

**Winner: AI by ~$14,751/month**

## When to Choose Each Option

**Choose Agency if:**
- Your spend is >$15,000/month
- You want strategic planning (brand positioning, market research)
- You don't want to think about ads
- You value relationships

**Choose AI if:**
- Your spend is <$10,000/month
- You want maximum ROI
- You're willing to spend 30 min/week reviewing
- You want transparency

**Choose DIY if:**
- You want to learn Google Ads
- Your spend is <$500/month
- You have 10+ hours/week
- You're patient with learning curves

## The Hybrid Model

Many successful companies do this:
- Use an agency for strategy (quarterly planning sessions)
- Use AI for execution (daily optimization)
- Spend: $1,000 agency + $249 AI = ~$1,265/month

This gives you the best of both worlds: strategic guidance + real-time optimization.

## The Bottom Line

**For most businesses under $10,000/month in ad spend: AI platforms win on ROI.**

They're 98% cheaper than agencies, optimize faster, and give you better results.

At $5,000/month spend, choosing an AI platform over an agency saves you $59,000/year while improving results.

That's not close to a fair fight.`
  },
  {
    id: "5",
    slug: "google-ads-for-agencies",
    title: "Google Ads for Agencies: Scale Your Client Management 10x",
    excerpt: "How to manage 50+ Google Ads accounts without hiring a massive team.",
    category: "Agency Tips",
    author: "George Woodworth",
    date: "2026-04-05",
    readTime: 9,
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
    keywords: ["Google Ads agencies", "agency management", "client management", "scaling"],
    relatedSlugs: ["reduce-google-ads-cpa", "budget-optimization-guide"],
    content: `# Google Ads for Agencies: Scale Your Client Management 10x

You're managing 20 Google Ads accounts. Your team is drowning.

Each account needs:
- Weekly optimization
- Monthly reporting
- Quarterly strategy sessions
- Client calls
- Troubleshooting

At 20 accounts, that's 40+ hours per week. You need to hire.

But what if you didn't?

What if one person could manage 200+ accounts with the right tools?

## The Agency Problem

**Current state**: Most agencies use this workflow:

1. Specialist creates campaigns (8 hours per account)
2. Junior analyst monitors and optimizes weekly (4 hours/week)
3. Senior analyst reviews monthly (2 hours/month)
4. Account manager handles client communication (5 hours/month)

**Per account cost**: ~$8,000-12,000/year in labor

**Revenue per account**: ~$3,000-5,000/year

**Result**: Unprofitable at scale

## The Solution: Automation + AI

Replace manual optimization with AI:

**New workflow**:
1. Specialist creates campaigns (8 hours, one-time)
2. AI monitors and optimizes (automated)
3. Junior analyst spot-checks weekly (0.5 hours/week)
4. Account manager handles communication (2 hours/month)

**Per account cost**: ~$2,000/year in labor + $249/year share of Upfixe Agency = ~$2,270/year

**Revenue per account**: $4,000/year

**Result**: 25% profit margin

Now one person can profitably manage 50+ accounts.

## How Agencies Scale with AI

### 1. Consolidate Reporting

Manual reporting takes 30% of your team's time.

Solution:
- Use automated dashboards
- Send weekly performance summaries automatically
- Highlight only key metrics

**Time saved per account**: 2 hours/month

**For 50 accounts**: 100 hours/month = 2.5 weeks of work saved

### 2. Automate Optimization

Manual optimization happens once per week. AI optimizes daily.

Setup:
- Connect accounts to AI platform
- Set KPIs (target ROAS, CPA)
- Let AI optimize automatically

**Result**: 20-30% better ROI without manual work

### 3. Implement Standardized Processes

Stop customizing everything for every client.

Standardize:
- Campaign structure
- Ad copy templates
- Landing page format
- Reporting cadence

**Time saved per account**: 10 hours per month

### 4. Use Templates and Playbooks

Create playbooks for:
- E-commerce accounts
- B2B service accounts
- SaaS accounts
- Local business accounts

New accounts launch in days, not weeks.

**Time saved**: 50% of setup time

## Case Study: Scaling from 20 to 100 Accounts

**Before AI**:
- Team: 4 people (1 senior, 3 junior)
- Accounts managed: 20
- Labor cost: $300,000/year
- Revenue: $100,000/year
- Profit: -$200,000

**After AI + Automation**:
- Team: 4 people (same)
- Accounts managed: 100
- Labor cost: $300,000/year
- AI platform cost: $950/month = $11,400/year
- Revenue: $400,000/year
- Profit: $88,600/year

**Difference**: +$288,600 annual profit

That's not an exaggeration. That's what happens when you automate optimization and standardize processes.

## Implementation Plan

### Month 1: Choose Your Tools
- AI optimization platform
- Automated reporting solution
- Dashboard tool (Google Data Studio)

### Month 2: Create Playbooks
- Standardized campaign structure
- Ad copy templates
- Landing page templates

### Month 3: Automate Everything
- Connect all accounts to AI
- Set up automated dashboards
- Configure alert rules

### Month 4: Optimize Your Team
- Junior analysts become account managers
- Account managers focus on strategy
- Senior analyst handles complex cases

### Month 5+: Scale
- Add 20-30 new accounts per month
- Same team, 5x more clients
- 5x more profit

## Client Communication Strategy

Clients worry: "Will AI replace personalized service?"

Solution:
- Monthly strategy calls with account manager
- Weekly performance summaries (automated)
- Quarterly business reviews
- Direct line for questions

**Result**: Clients feel more supported, not less.

## Pricing Strategy for Scaled Agencies

With automation, you can:
1. Reduce prices (undercut competitors)
2. Increase margins (same price, higher profit)
3. Offer tiered service (basic automation, premium with strategy)

**Recommended approach**: Tiered service
- **Basic**: $500/month (AI-managed, weekly reports)
- **Standard**: $1,500/month (AI-managed + monthly strategy call)
- **Premium**: $3,000/month (AI-managed + weekly strategy, dedicated account manager)

## The Bottom Line

Agencies using AI + automation are:
- Managing 5-10x more clients per person
- Making 3-5x more profit per account
- Delivering better results (24/7 optimization vs weekly)
- Growing 2-3x faster than competitors

If you're not using AI to manage Google Ads, you're leaving money on the table.

Lots of it.`
  }
];

export const getBlogPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find((post) => post.slug === slug);
};

export const getBlogPostsByCategory = (category: string): BlogPost[] => {
  return blogPosts.filter((post) => post.category === category);
};

export const getRelatedPosts = (slug: string): BlogPost[] => {
  const post = getBlogPostBySlug(slug);
  if (!post) return [];
  return post.relatedSlugs
    .map((relatedSlug) => getBlogPostBySlug(relatedSlug))
    .filter((p): p is BlogPost => p !== undefined);
};

// ── Supabase-backed hooks ────────────────────────────────────────────
//
// New auto-published experiment posts come from Supabase. The hardcoded
// array above is the fallback / seed for older posts. Supabase rows take
// precedence on slug collision.

import { useEffect, useState } from "react";
import { getSupabaseClient } from "@/lib/supabase";

interface SupabaseBlogRow {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content_markdown: string;
  category: string;
  author: string;
  published_at: string;
  read_time: number;
  image_url: string | null;
  keywords: string[];
  related_slugs: string[];
  pdf_url: string | null;
  csv_urls: { label: string; url: string }[];
  experiment_type: string;
}

const DEFAULT_IMAGE_BY_TYPE: Record<string, string> = {
  storm: "/blog/storm-default.svg",
  breakdown: "/blog/breakdown-default.svg",
};

function mapSupabaseRow(row: SupabaseBlogRow): BlogPost {
  return {
    id: `sb-${row.id}`,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt,
    content: row.content_markdown,
    category: row.category,
    author: row.author,
    date: row.published_at.split("T")[0],
    readTime: row.read_time,
    image:
      row.image_url || DEFAULT_IMAGE_BY_TYPE[row.experiment_type] ||
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    keywords: row.keywords || [],
    relatedSlugs: row.related_slugs || [],
    pdfUrl: row.pdf_url || undefined,
    csvDownloads: row.csv_urls || [],
    experimentType: row.experiment_type,
  };
}

function mergeBySlug(supabase: BlogPost[], local: BlogPost[]): BlogPost[] {
  const supabaseSlugs = new Set(supabase.map((p) => p.slug));
  const localFiltered = local.filter((p) => !supabaseSlugs.has(p.slug));
  return [...supabase, ...localFiltered].sort((a, b) =>
    b.date.localeCompare(a.date),
  );
}

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>(() =>
    [...blogPosts].sort((a, b) => b.date.localeCompare(a.date)),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sb = getSupabaseClient();
    if (!sb) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data, error: err } = await sb
        .from("blog_posts")
        .select(
          "id,slug,title,excerpt,content_markdown,category,author,published_at,read_time,image_url,keywords,related_slugs,pdf_url,csv_urls,experiment_type",
        )
        .order("published_at", { ascending: false });
      if (cancelled) return;
      if (err) {
        console.error("[blogPosts] fetch failed", err.message);
        setError(err.message);
        setLoading(false);
        return;
      }
      const mapped = (data as SupabaseBlogRow[]).map(mapSupabaseRow);
      setPosts(mergeBySlug(mapped, blogPosts));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
}

export function useBlogPost(slug: string | undefined) {
  const [post, setPost] = useState<BlogPost | undefined>(() =>
    slug ? getBlogPostBySlug(slug) : undefined,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const sb = getSupabaseClient();
    if (!sb) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data, error: err } = await sb
        .from("blog_posts")
        .select(
          "id,slug,title,excerpt,content_markdown,category,author,published_at,read_time,image_url,keywords,related_slugs,pdf_url,csv_urls,experiment_type",
        )
        .eq("slug", slug)
        .maybeSingle();
      if (cancelled) return;
      if (err || !data) {
        // Fall back to hardcoded if Supabase has no row for this slug
        setLoading(false);
        return;
      }
      setPost(mapSupabaseRow(data as SupabaseBlogRow));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { post, loading };
}
